import { PASSWORD_MIN_LENGTH, type SessionUser } from '@itin/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Button, Card, ErrorNotice, Field, Input } from '../components/ui.js'
import { api, ApiError } from '../lib/api.js'
import { useT } from '../lib/i18n.js'

/**
 * Everything an advisor can do to their own account, which today is one thing.
 *
 * A page rather than another control in the nav: the nav holds two selects that
 * take one tap each, and it only opens as a disclosure on a phone. A form with
 * three fields and a consequence — every other device signed out — needs room
 * to say so before it is submitted.
 */
export function AccountPage() {
  const t = useT()
  const session = useQuery({ queryKey: ['me'], queryFn: api.me })

  return (
    <main className="flex flex-col gap-5">
      <h1 className="font-display text-2xl font-semibold tracking-tight">{t('account.title')}</h1>
      {session.data && (
        <p className="text-sm text-slate-500">
          {t('account.signedInAs', { email: session.data.user.email })}
        </p>
      )}
      <ChangePasswordCard user={session.data?.user} />
    </main>
  )
}

function ChangePasswordCard({ user }: { user: SessionUser | undefined }) {
  const t = useT()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () => api.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess() {
      // The session survives the change, so there is nothing to refetch and
      // nowhere to navigate. Clearing the fields is the whole cleanup.
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(true)
    },
  })

  // Checked here and not sent: typing it twice defends against a typo in a
  // field you cannot read, which is a property of this form rather than of the
  // account. The server takes two values, so there is no third rule the two
  // sides could disagree about.
  const mismatch = confirm.length > 0 && next !== confirm
  const tooShort = next.length > 0 && next.length < PASSWORD_MIN_LENGTH
  const sameAsCurrent = next.length > 0 && next === current
  const submittable =
    current.length > 0 && next.length >= PASSWORD_MIN_LENGTH && next === confirm && !sameAsCurrent

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!submittable) return
    setDone(false)
    change.mutate()
  }

  return (
    <Card title={t('account.password.title')} testId="change-password">
      <form onSubmit={submit} className="flex max-w-sm flex-col gap-4">
        {/*
          Present, hidden, and holding the signed-in address. Password managers
          file a credential under the account it belongs to, and a change form
          with no username field either saves nothing or saves the new password
          against the wrong entry.
        */}
        <input type="hidden" name="username" autoComplete="username" value={user?.email ?? ''} />

        <Field
          label={t('account.password.current')}
          guidance="account.password.current.guidance"
          error={
            change.isError && isWrongPassword(change.error)
              ? t('account.password.wrongCurrent')
              : undefined
          }
        >
          <Input
            type="password"
            name="currentPassword"
            value={current}
            autoComplete="current-password"
            required
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>

        <Field
          label={t('account.password.new')}
          hint={t('account.password.lengthHint', { min: PASSWORD_MIN_LENGTH })}
          guidance="account.password.new.guidance"
          error={
            tooShort
              ? t('account.password.tooShort', { min: PASSWORD_MIN_LENGTH })
              : sameAsCurrent
                ? t('account.password.sameAsCurrent')
                : undefined
          }
        >
          <Input
            type="password"
            name="newPassword"
            value={next}
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
            required
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>

        <Field
          label={t('account.password.confirm')}
          error={mismatch ? t('account.password.mismatch') : undefined}
        >
          <Input
            type="password"
            name="confirmPassword"
            value={confirm}
            autoComplete="new-password"
            required
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        {/*
          A wrong current password arrives as the same 401 as a session that has
          expired, and the server cannot tell the client which without also
          telling anyone else who asks. This form is only reachable with a live
          session, so the far more likely reading is named here; a session that
          really has expired is caught by the shell on the next navigation.
        */}
        {change.isError && !isWrongPassword(change.error) && <ErrorNotice error={change.error} />}

        {done && (
          <p
            role="status"
            className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
          >
            {t('account.password.changed')}
          </p>
        )}

        <Button type="submit" disabled={!submittable || change.isPending}>
          {change.isPending ? t('common.loading') : t('account.password.submit')}
        </Button>
      </form>
    </Card>
  )
}

function isWrongPassword(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'unauthorized'
}
